/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import Pagination from 'components/atoms/pagination';
import AiManagementCardMenu from 'components/molecules/ai_management_card_menu';
import AiManagementCardModal from 'components/organisms/ai_management_card_modal';
import AiManagementUpdateEditalFiles from 'components/molecules/ai_management_update_edital_files';
import {
  dismissUpdates,
  listUpdates,
  markUpdatesRead,
  markUpdatesUnread,
} from 'services/actions/ai_management_updates';
import {
  UPDATES_PER_PAGE,
  UPDATE_KIND_ICON,
  UPDATE_FILTERS,
  AiManagementUpdate,
  UpdateKind,
} from 'services/ai_management_updates';
import { formatDateTimeLong } from 'services/ai_management_dates';
import { useAiManagementCardModal } from 'hooks/useAiManagementCardModal';
import { Container } from './styles';

const metaLine = (update: AiManagementUpdate) => {
  const { notice_number: noticeNumber, public_body: publicBody, city, state } = update.bidding;

  return [noticeNumber, publicBody, [city, state].filter(Boolean).join(' - ')]
    .filter(Boolean)
    .join(' • ');
};

interface AiManagementUpdatesProps {
  onUnreadCountChange?: (count: number) => void;
}

const AiManagementUpdates: React.FC<AiManagementUpdatesProps> = ({ onUnreadCountChange }) => {
  const [kind, setKind] = useState<UpdateKind | 'todas'>('todas');
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [updates, setUpdates] = useState<AiManagementUpdate[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [acting, setActing] = useState(false);
  const cardModal = useAiManagementCardModal();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);

    listUpdates({
      page,
      per_page: UPDATES_PER_PAGE,
      ...(kind === 'todas' ? {} : { kind }),
      ...(onlyUnread ? { read: false } : {}),
    })
      .then((response) => {
        if (!active) return;
        if (response?.status !== 200 || !Array.isArray(response.data?.updates)) {
          setError(true);
          return;
        }
        setUpdates(response.data.updates);
        setTotalEntries(response.data.total_entries ?? response.data.updates.length);
        if (onUnreadCountChange) onUnreadCountChange(response.data.unread_count ?? 0);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [kind, onlyUnread, page, reloadKey]);

  const handleCloseCard = () => {
    cardModal.close();
    setReloadKey((key) => key + 1);
  };

  const resetListState = () => {
    setPage(1);
    setSelected(new Set());
    setExpanded(new Set());
  };

  const selectKind = (next: UpdateKind | 'todas') => {
    setKind(next);
    resetListState();
  };

  const toggleOnlyUnread = () => {
    setOnlyUnread((prev) => !prev);
    resetListState();
  };

  const goToPage = (next: number) => {
    setPage(next);
    setSelected(new Set());
    setExpanded(new Set());
  };

  const applyUnreadCount = (count: unknown) => {
    if (onUnreadCountChange && typeof count === 'number') onUnreadCountChange(count);
  };

  const reloadWithout = (ids: number[]) => {
    const remaining = updates.filter((update) => !ids.includes(update.id)).length;
    if (remaining === 0 && page > 1) setPage(page - 1);
    else setReloadKey((key) => key + 1);
  };

  const applyReadState = (read: boolean) => {
    const ids = Array.from(selected);
    if (!ids.length) return;

    setActing(true);
    (read ? markUpdatesRead(ids) : markUpdatesUnread(ids))
      .then((response) => {
        if (!response || response.status >= 400) return;
        applyUnreadCount(response.data?.unread_count);
        setSelected(new Set());

        if (onlyUnread) reloadWithout(ids);
        else
          setUpdates((prev) =>
            prev.map((update) => (ids.includes(update.id) ? { ...update, read } : update)),
          );
      })
      .finally(() => setActing(false));
  };

  const handleMarkRead = () => applyReadState(true);
  const handleMarkUnread = () => applyReadState(false);

  const handleDismiss = () => {
    const ids = Array.from(selected);
    if (!ids.length) return;

    const target = ids.length === 1 ? 'esta atualização' : `estas ${ids.length} atualizações`;
    if (!window.confirm(`Excluir ${target} da sua lista? Esta ação não pode ser desfeita.`)) return;

    setActing(true);
    dismissUpdates(ids)
      .then((response) => {
        if (!response || response.status >= 400) return;
        applyUnreadCount(response.data?.unread_count);
        setSelected(new Set());
        setExpanded(new Set());
        reloadWithout(ids);
      })
      .finally(() => setActing(false));
  };

  const pageIds = updates.map((update) => update.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleIn = (set: Set<number>, id: number) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const toggleOne = (id: number) => setSelected((prev) => toggleIn(prev, id));
  const toggleExpanded = (id: number) => setExpanded((prev) => toggleIn(prev, id));

  const toggleAllPage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  const renderList = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Carregando...</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-5">
          <p className="text-muted mb-2">Não foi possível carregar as atualizações.</p>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Tentar novamente
          </button>
        </div>
      );
    }

    if (updates.length === 0) {
      return (
        <div className="text-center text-muted py-4">
          {onlyUnread
            ? 'Nenhuma atualização não lida por aqui.'
            : 'Nenhuma atualização por aqui ainda.'}
        </div>
      );
    }

    return updates.map((update) => {
      const isExpanded = expanded.has(update.id);

      return (
        <div
          key={update.id}
          className={`update-card card border mb-2 ${update.read ? 'is-read' : 'is-unread'}`}
        >
          <div className="d-flex align-items-center p-3">
            <input
              type="checkbox"
              className="mr-3 flex-shrink-0"
              checked={selected.has(update.id)}
              onChange={() => toggleOne(update.id)}
              aria-label={`Selecionar ${update.title}`}
            />

            <span className="update-icon rounded d-flex align-items-center justify-content-center flex-shrink-0 mr-3">
              <i className="material-icons">{UPDATE_KIND_ICON[update.kind]}</i>
            </span>

            <div className="flex-grow-1 min-width-0">
              <div className="d-flex align-items-center flex-wrap">
                <span className={`update-title mr-2${update.read ? '' : ' font-weight-bold'}`}>
                  {update.title}
                </span>
                {!update.read && <span className="badge badge-danger">Novo</span>}
              </div>
              <div className="update-meta text-muted text-truncate">{metaLine(update)}</div>
            </div>

            <div className="d-flex align-items-center flex-shrink-0 ml-3">
              <span className="update-date text-muted text-nowrap mr-3">
                {formatDateTimeLong(update.published_at)}
              </span>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mr-2 text-nowrap d-flex align-items-center"
                disabled={cardModal.opening !== null}
                onClick={() => cardModal.openCard({ cardId: update.card_id })}
              >
                Acessar licitação
                {cardModal.opening?.cardId === update.card_id && (
                  <span className="spinner-border spinner-border-sm ml-2" role="status" />
                )}
              </button>
              {update.description ? (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm d-flex align-items-center"
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? 'Recolher' : 'Expandir'} ${update.title}`}
                  onClick={() => toggleExpanded(update.id)}
                >
                  <i className="material-icons">{isExpanded ? 'expand_less' : 'expand_more'}</i>
                </button>
              ) : (
                <span
                  className="btn btn-secondary btn-sm d-flex align-items-center disabled"
                  aria-hidden="true"
                >
                  <i className="material-icons">chevron_right</i>
                </span>
              )}
            </div>
          </div>

          {isExpanded && update.description && (
            <div className="update-body border-top px-3 py-3">
              {update.description}
              {update.kind === 'notice' && update.bidding.id ? (
                <AiManagementUpdateEditalFiles biddingId={update.bidding.id} />
              ) : null}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <Container className="py-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div className="d-flex flex-wrap">
          {UPDATE_FILTERS.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`update-chip btn btn-sm rounded-pill mr-2 mb-2 ${
                kind === filter.key ? 'btn-dark' : 'btn-outline-secondary'
              }`}
              onClick={() => selectKind(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="d-flex flex-wrap">
          <button
            type="button"
            className={`btn btn-sm rounded-pill mb-2 d-flex align-items-center ${
              onlyUnread ? 'btn-dark' : 'btn-outline-secondary'
            }`}
            aria-pressed={onlyUnread}
            onClick={toggleOnlyUnread}
          >
            <i className="material-icons mr-1">
              {onlyUnread ? 'check_box' : 'check_box_outline_blank'}
            </i>
            Somente não lidas
          </button>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <label className="mb-0 d-flex align-items-center" htmlFor="select-all-updates">
          <input
            id="select-all-updates"
            type="checkbox"
            className="mr-2"
            checked={allPageSelected}
            onChange={toggleAllPage}
            disabled={pageIds.length === 0}
          />
          Selecionar todas as atualizações da página
        </label>

        <div className="d-flex">
          <AiManagementCardMenu
            ariaLabel="Ações em massa"
            label="Ações"
            disabled={selected.size === 0 || acting}
            items={[
              { label: 'Marcar como lida', onClick: handleMarkRead },
              { label: 'Marcar como não lida', onClick: handleMarkUnread },
              { label: 'Excluir atualização', onClick: handleDismiss },
            ]}
          />
        </div>
      </div>

      {renderList()}

      {!loading && !error && totalEntries > UPDATES_PER_PAGE && (
        <Pagination
          postsPerPage={UPDATES_PER_PAGE}
          totalPosts={totalEntries}
          currentPage={page}
          paginate={goToPage}
          firstAndLastPage
        />
      )}

      {cardModal.card && <AiManagementCardModal card={cardModal.card} onClose={handleCloseCard} />}
    </Container>
  );
};

export default AiManagementUpdates;
